import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  incomeExpenseApi,
  ProjectBankAccount,
  ProjectBankStatementLineRow,
  ProjectBankStatementImportLogRow,
  ProjectBankStatementPeriodDetail,
  ProjectBankStatementPeriodSummary,
  ProjectBankStatementSurplusGapOption,
  ProjectLedgerRow,
} from '../../../api/incomeExpenseApi';
import LedgerVoucherModal from '../../../components/modals/LedgerVoucherModal';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import ImportFileLink from '../../../components/common/ImportFileLink';
import ApartmentServiceFeePaymentModal from '../../../components/modals/ApartmentServiceFeePaymentModal';
import { paymentApi } from '../../../api/paymentApi';
import CreateActivityVoucherModal, { ActivityVoucherPrefill } from './CreateActivityVoucherModal';
import { exportBankStatementPeriodReport } from '../bankStatementExport';

const extractApartmentCodeFromStatementContent = (content: string): string | null => {
  const upper = content.toUpperCase();
  const match = upper.match(/\b([A-Z]\d{4,5})\b/);
  return match ? match[1] : null;
};

const formatCurrency = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const toDateInputValue = (iso: string) => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const statusLabel: Record<string, string> = {
  MATCHED: 'Đã khớp',
  UNMATCHED: 'Chưa khớp',
  IGNORED: 'Bỏ qua',
};

const resolveLedgerVoucherTitle = (row: ProjectLedgerRow): string => {
  if (row.viewType === 'fee_receipt') return 'Phiếu thu chi (Quản lý thu chi)';
  if (row.viewType === 'offset_voucher') return 'Cấn trừ phiếu thu';
  if (row.viewType === 'surplus_voucher' && row.voucherName === 'Nhận tiền từ chuyển khoản online') {
    return 'Nhận tiền từ chuyển khoản online';
  }
  if (row.viewType === 'surplus_voucher') return 'Đóng dư phiếu thu';
  if (row.viewType === 'manual_voucher') return 'Phiếu thu/chi tiện ích';
  return 'Xem phiếu';
};

const statusClass: Record<string, string> = {
  MATCHED: 'bg-green-100 text-green-800',
  UNMATCHED: 'bg-amber-100 text-amber-800',
  IGNORED: 'bg-gray-100 text-gray-600',
};

const ROW_ACTION_READONLY_TITLE =
  'Thao tác ghép / tạo phiếu chỉ thực hiện tại Quản lý thu chi → Tài khoản ngân hàng → Tài khoản dự án → Sổ phụ tài khoản.';

const SubLedgerRowAction: React.FC<{
  readOnly?: boolean;
  className: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ readOnly, className, title, onClick, children }) => {
  if (readOnly) {
    return (
      <span
        className={`${className} opacity-50 cursor-not-allowed select-none`}
        title={title ?? ROW_ACTION_READONLY_TITLE}
      >
        {children}
      </span>
    );
  }
  return (
    <button type="button" className={className} title={title} onClick={onClick}>
      {children}
    </button>
  );
};

interface ProjectBankSubLedgerPanelProps {
  account: ProjectBankAccount;
  buildingId: string;
  /** When true, row actions are visible but disabled (import + auto-match still work). */
  readOnlyRowActions?: boolean;
}

export const ProjectBankSubLedgerPanel: React.FC<ProjectBankSubLedgerPanelProps> = ({
  account,
  buildingId,
  readOnlyRowActions = false,
}) => {
  const now = new Date();
  const [periods, setPeriods] = useState<ProjectBankStatementPeriodSummary[]>([]);
  const [importHistory, setImportHistory] = useState<ProjectBankStatementImportLogRow[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [importYear, setImportYear] = useState(now.getFullYear());
  const [importMonth, setImportMonth] = useState(now.getMonth() + 1);
  const [detail, setDetail] = useState<ProjectBankStatementPeriodDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'UNMATCHED' | 'MATCHED' | 'IGNORED'>('all');
  const [manualMatchLineId, setManualMatchLineId] = useState<string | null>(null);
  const [manualLedgerId, setManualLedgerId] = useState('');
  const [createVoucher, setCreateVoucher] = useState<{
    kind: 'revenue' | 'expenditure';
    line: ProjectBankStatementLineRow;
  } | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [feePaymentModal, setFeePaymentModal] = useState<{
    apartmentId: string;
    apartmentCode: string;
    line: ProjectBankStatementLineRow;
  } | null>(null);
  const [surplusModal, setSurplusModal] = useState<{
    line: ProjectBankStatementLineRow;
    options: ProjectBankStatementSurplusGapOption[];
    billId: string;
  } | null>(null);
  const [surplusSaving, setSurplusSaving] = useState(false);
  const [voucherPreview, setVoucherPreview] = useState<{
    entryId: string;
    title: string;
    mode?: 'fee' | 'surplus';
  } | null>(null);

  const loadImportHistory = async () => {
    try {
      const rows = await incomeExpenseApi.listProjectBankStatementImportHistory(account.id);
      setImportHistory(rows);
    } catch {
      setImportHistory([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await incomeExpenseApi.listProjectBankStatementPeriods(account.id);
        if (cancelled) return;
        setPeriods(rows);
        setSelectedPeriodId((prev) => {
          if (prev && rows.some((r) => r.id === prev)) return prev;
          return rows[0]?.id ?? '';
        });
        await loadImportHistory();
      } catch (error: unknown) {
        if (!cancelled) {
          toast.error(
            getApiErrorMessage(
              error,
              'Không thể tải kỳ sao kê — chạy migrate DB (project bank statement) nếu môi trường mới',
            ),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account.id]);

  useEffect(() => {
    if (!selectedPeriodId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    incomeExpenseApi
      .getProjectBankStatementPeriod(selectedPeriodId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Không thể tải sổ phụ tài khoản');
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPeriodId]);

  const filteredLines = useMemo(() => {
    if (!detail) return [];
    if (filter === 'all') return detail.lines;
    return detail.lines.filter((line) => line.status === filter);
  }, [detail, filter]);

  const unmatchedLedgerOptions = useMemo(() => {
    if (!detail) return [];
    return detail.ledgerEntries.filter((row) => !row.matchedStatementLineId && row.bankIncome + row.bankExpense > 0);
  }, [detail]);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2];
  }, [now]);

  const statementBankLabel = useMemo(
    () => account.bankName?.trim() || account.name?.trim() || 'ngân hàng',
    [account.bankName, account.name],
  );

  const refreshPeriodsAndDetail = async (periodId?: string) => {
    const rows = await incomeExpenseApi.listProjectBankStatementPeriods(account.id);
    setPeriods(rows);
    await loadImportHistory();
    const id = periodId ?? selectedPeriodId;
    if (id) {
      setDetail(await incomeExpenseApi.getProjectBankStatementPeriod(id));
      setSelectedPeriodId(id);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await incomeExpenseApi.importProjectBankStatement(
        account.id,
        file,
        importYear,
        importMonth,
      );
      toast.success(`Đã nhập sao kê ${result.periodMonth}/${result.periodYear}`);
      setImportYear(result.periodYear);
      setImportMonth(result.periodMonth);
      await refreshPeriodsAndDetail(result.id);
      setImportModalOpen(false);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể nhập sao kê'));
    } finally {
      setUploading(false);
    }
  };

  const openSurplusGapForLine = async (line: ProjectBankStatementLineRow) => {
    try {
      const options = await incomeExpenseApi.listProjectBankStatementSurplusGapOptions(line.id);
      if (options.length === 0) {
        toast.error('Không có phiếu thu CK thiếu phần thu dư so với số tiền trên sao kê');
        return;
      }
      setSurplusModal({
        line,
        options,
        billId: options.length === 1 ? options[0].billId : '',
      });
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể tính chênh lệch thu dư'));
    }
  };

  const openFeePaymentForLine = async (line: ProjectBankStatementLineRow) => {
    const code = extractApartmentCodeFromStatementContent(line.content);
    if (!code) {
      toast.error('Không đọc được mã căn hộ trong nội dung CK');
      return;
    }
    try {
      const matches = await paymentApi.searchApartmentMatches(buildingId, code);
      const apartment =
        matches.find((m) => m.code.toUpperCase() === code.toUpperCase()) ?? matches[0];
      if (!apartment) {
        toast.error(`Không tìm thấy căn hộ ${code} trong dự án`);
        return;
      }
      setFeePaymentModal({
        apartmentId: apartment.id,
        apartmentCode: apartment.code,
        line,
      });
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể mở thanh toán phí'));
    }
  };

  const buildPrefill = (line: ProjectBankStatementLineRow): ActivityVoucherPrefill => ({
    amount: line.amount,
    actualDate: toDateInputValue(line.transactionAt),
    reason: line.content.slice(0, 500),
    name: line.content.slice(0, 120),
    projectBankAccountId: account.id,
  });

  const handleVoucherCreated = async (
    lineId: string,
    result: { activityLedgerEntryId: string },
  ) => {
    try {
      const data = await incomeExpenseApi.matchProjectBankStatementLine(
        lineId,
        result.activityLedgerEntryId,
      );
      setDetail(data);
      toast.success('Đã tạo phiếu và ghép với sao kê');
    } catch {
      toast.success('Đã tạo phiếu — ghép thủ công nếu cần');
      if (selectedPeriodId) {
        setDetail(await incomeExpenseApi.getProjectBankStatementPeriod(selectedPeriodId));
      }
    }
    setCreateVoucher(null);
  };

  const handleCancelMatched = async (line: ProjectBankStatementLineRow) => {
    const ledgerId = line.matchedLedger?.ledgerEntryId;
    if (!ledgerId || !line.matchedLedger?.canCancelManualVoucher) return;
    if (!window.confirm('Huỷ phiếu thu/chi đã ghép với dòng sao kê này?')) return;
    try {
      if (line.matchedLedger.entryType === 'MANUAL_REVENUE') {
        await incomeExpenseApi.cancelRevenueVoucherByLedgerEntry(ledgerId);
      } else {
        await incomeExpenseApi.cancelExpenditureVoucherByLedgerEntry(ledgerId);
      }
      await incomeExpenseApi.unmatchProjectBankStatementLine(line.id);
      toast.success('Đã huỷ phiếu');
      if (selectedPeriodId) {
        setDetail(await incomeExpenseApi.getProjectBankStatementPeriod(selectedPeriodId));
      }
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể huỷ phiếu'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-cyan-50 border border-cyan-100 rounded-lg px-4 py-3 text-sm text-cyan-900">
        Tải file sao kê <strong>{statementBankLabel}</strong> (.xls/.xlsx) qua <strong>Tải sao kê</strong>, chọn kỳ trong form nhập.
        Dòng <strong>Chi lãi TK tiền gửi</strong> là thu lãi — <strong>Tạo phiếu thu</strong>.
        Thu phí căn hộ chưa có trên sổ — <strong>Thanh toán phí</strong> (ghi nhận thu phí dịch vụ, không qua Hoạt động thu).
        Chỉ giao dịch tất toán / rút trước lãi được tự động bỏ qua.
        {readOnlyRowActions && (
          <span className="block mt-2 text-cyan-800">
            Trên màn hình này chỉ <strong>Tải sao kê</strong> và <strong>Ghép tự động lại</strong> được bật; các thao tác
            trên từng dòng (tạo phiếu, ghép thủ công, …) thực hiện tại{' '}
            <strong>Tài khoản ngân hàng → Tài khoản dự án → Sổ phụ tài khoản</strong>.
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-lg p-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Kỳ đã nhập</label>
          <select
            value={selectedPeriodId}
            onChange={(e) => setSelectedPeriodId(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm min-w-[220px]"
          >
            <option value="">
              {periods.length === 0 ? 'Chưa có — bấm Tải sao kê' : '-- Chọn kỳ đã nhập --'}
            </option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {String(p.periodMonth).padStart(2, '0')}/{p.periodYear} ({p.lineCount} dòng)
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setImportModalOpen(true)}
          className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600"
        >
          Tải sao kê
        </button>
        {selectedPeriodId && (
          <>
            <button
              type="button"
              title="Ghép lại Chưa khớp với sổ thu chi; đưa các dòng tự động Bỏ qua (cũ) như CHI LAI về Chưa khớp. Cộng phát sinh: tải lại file cùng kỳ để lấy số tổng từ ngân hàng."
              onClick={async () => {
                try {
                  const data = await incomeExpenseApi.autoMatchProjectBankStatement(selectedPeriodId);
                  setDetail(data);
                  toast.success('Đã cập nhật ghép tự động và thống kê đối chiếu');
                } catch (error: unknown) {
                  toast.error(getApiErrorMessage(error, 'Ghép tự động thất bại'));
                }
              }}
              className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
            >
              Ghép tự động lại
            </button>
            <button
              type="button"
              disabled={!detail}
              onClick={() => {
                if (!detail) return;
                try {
                  exportBankStatementPeriodReport({
                    accountName: account.name,
                    detail,
                  });
                  toast.success('Đã xuất báo cáo');
                } catch {
                  toast.error('Không thể xuất báo cáo');
                }
              }}
              className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              Xuất báo cáo
            </button>
          </>
        )}
      </div>

      {importHistory.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Lịch sử tải sao kê</h4>
          <div className="overflow-x-auto max-h-40 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left py-1 pr-3 font-medium">Thời gian</th>
                  <th className="text-left py-1 pr-3 font-medium">Kỳ</th>
                  <th className="text-left py-1 pr-3 font-medium">File</th>
                  <th className="text-left py-1 pr-3 font-medium">Người tải</th>
                  <th className="text-right py-1 font-medium">Dòng</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{formatDateTime(row.uploadedAt)}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      {String(row.periodMonth).padStart(2, '0')}/{row.periodYear}
                    </td>
                    <td className="py-1.5 pr-3 max-w-xs truncate">
                      <ImportFileLink fileName={row.sourceFileName} fileUrl={row.fileUrl} />
                    </td>
                    <td className="py-1.5 pr-3">{row.uploader?.name || '—'}</td>
                    <td className="py-1.5 text-right">{row.lineCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <div className="bg-white border rounded-lg p-4 text-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
          <div>
            <span className="text-gray-500">Số tài khoản: </span>
            <span className="font-medium">
              {detail.statementBankAccountNumber || account.bankAccountNumber || '—'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Số dư đầu kỳ: </span>
            <span className="font-medium">
              {detail.openingBalance != null ? formatCurrency(detail.openingBalance) : '—'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Số dư cuối kỳ: </span>
            <span className="font-medium">
              {detail.closingBalance != null ? formatCurrency(detail.closingBalance) : '—'}
            </span>
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <span className="text-gray-500">Cộng phát sinh trong kỳ: </span>
            <span className="font-medium">
              Rút{' '}
              {detail.periodTotalWithdrawal != null
                ? formatCurrency(detail.periodTotalWithdrawal)
                : '—'}{' '}
              · Gửi{' '}
              {detail.periodTotalDeposit != null ? formatCurrency(detail.periodTotalDeposit) : '—'}
            </span>
          </div>
        </div>
      )}

      {detail && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="bg-white border rounded-lg p-3">
            <div className="text-gray-500">Đã khớp</div>
            <div className="text-lg font-semibold text-green-700">{detail.summary.matchedInBank}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-gray-500">Chưa khớp (sao kê)</div>
            <div className="text-lg font-semibold text-amber-700">{detail.summary.unmatchedInBank}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-gray-500">Thiếu trên hệ thống</div>
            <div className="text-lg font-semibold text-red-700">{detail.summary.missedInSystem}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-gray-500">Thiếu trên sao kê</div>
            <div className="text-lg font-semibold text-red-700">{detail.summary.missedInBank}</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(['all', 'UNMATCHED', 'MATCHED', 'IGNORED'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              filter === key ? 'bg-cyan-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {key === 'all' ? 'Tất cả' : statusLabel[key]}
          </button>
        ))}
      </div>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Ngày GD</th>
              <th className="px-3 py-2 text-left">Nội dung</th>
              <th className="px-3 py-2 text-right">Gửi</th>
              <th className="px-3 py-2 text-right">Rút</th>
              <th className="px-3 py-2 text-left">Trạng thái</th>
              <th className="px-3 py-2 text-left">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">Đang tải...</td>
              </tr>
            ) : filteredLines.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {selectedPeriodId
                    ? 'Không có dòng phù hợp bộ lọc'
                    : 'Chọn kỳ đã nhập hoặc tải sao kê mới'}
                </td>
              </tr>
            ) : (
              filteredLines.map((line) => (
                <tr key={line.id} className="border-t align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(line.transactionAt)}</td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="line-clamp-3" title={line.content}>{line.content}</div>
                    {line.bankReference && (
                      <div className="text-xs text-gray-500 mt-1">{line.bankReference}</div>
                    )}
                    {line.matchedLedger?.journalNumber && (
                      <div className="text-xs text-green-700 mt-1">Phiếu: {line.matchedLedger.journalNumber}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-cyan-700">
                    {line.depositAmount ? formatCurrency(line.depositAmount) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-red-700">
                    {line.withdrawalAmount ? formatCurrency(line.withdrawalAmount) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${statusClass[line.status]}`}>
                      {statusLabel[line.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex flex-col gap-1 items-start">
                      {line.status === 'UNMATCHED' && (
                        <>
                          <SubLedgerRowAction
                            readOnly={readOnlyRowActions}
                            className="text-xs text-orange-600 font-medium"
                            onClick={() =>
                              setCreateVoucher({
                                kind: line.direction === 'DEPOSIT' ? 'revenue' : 'expenditure',
                                line,
                              })
                            }
                          >
                            {line.direction === 'DEPOSIT' ? 'Tạo phiếu thu' : 'Tạo phiếu chi'}
                          </SubLedgerRowAction>
                          {line.direction === 'DEPOSIT' && (
                            <SubLedgerRowAction
                              readOnly={readOnlyRowActions}
                              title="Thanh toán phí dịch vụ / ghi nhận thu phí căn hộ"
                              className="text-xs text-emerald-700 font-medium"
                              onClick={() => openFeePaymentForLine(line)}
                            >
                              Thanh toán phí
                            </SubLedgerRowAction>
                          )}
                          {line.hasSurplusGapAction && (
                            <SubLedgerRowAction
                              readOnly={readOnlyRowActions}
                              title="Ghi đóng dư CK khi khách chuyển thừa so với net sau cấn trừ (vd. 628đ)"
                              className="text-xs text-violet-700 font-medium"
                              onClick={() => openSurplusGapForLine(line)}
                            >
                              Thu dư
                            </SubLedgerRowAction>
                          )}
                          <SubLedgerRowAction
                            readOnly={readOnlyRowActions}
                            className="text-xs text-cyan-700"
                            onClick={() => {
                              setManualMatchLineId(line.id);
                              setManualLedgerId('');
                            }}
                          >
                            Ghép thủ công
                          </SubLedgerRowAction>
                          <SubLedgerRowAction
                            readOnly={readOnlyRowActions}
                            className="text-xs text-gray-600"
                            onClick={async () => {
                              try {
                                const data = await incomeExpenseApi.ignoreProjectBankStatementLine(line.id);
                                setDetail(data);
                              } catch (error: unknown) {
                                toast.error(getApiErrorMessage(error, 'Không thể bỏ qua'));
                              }
                            }}
                          >
                            Bỏ qua
                          </SubLedgerRowAction>
                        </>
                      )}
                      {line.status === 'IGNORED' && (
                        <SubLedgerRowAction
                          readOnly={readOnlyRowActions}
                          className="text-xs text-cyan-700"
                          onClick={async () => {
                            try {
                              const data = await incomeExpenseApi.restoreProjectBankStatementLine(line.id);
                              setDetail(data);
                            } catch (error: unknown) {
                              toast.error(getApiErrorMessage(error, 'Không thể khôi phục dòng'));
                            }
                          }}
                        >
                          Đưa về chưa khớp
                        </SubLedgerRowAction>
                      )}
                      {line.status === 'MATCHED' && (
                        <>
                          {line.matchedLedger?.canCancelManualVoucher && (
                            <SubLedgerRowAction
                              readOnly={readOnlyRowActions}
                              className="text-xs text-red-600"
                              onClick={() => handleCancelMatched(line)}
                            >
                              Huỷ phiếu
                            </SubLedgerRowAction>
                          )}
                          <SubLedgerRowAction
                            readOnly={readOnlyRowActions}
                            className="text-xs text-gray-600 hover:text-gray-900"
                            onClick={async () => {
                              try {
                                const data = await incomeExpenseApi.unmatchProjectBankStatementLine(line.id);
                                setDetail(data);
                              } catch (error: unknown) {
                                toast.error(getApiErrorMessage(error, 'Không thể bỏ ghép'));
                              }
                            }}
                          >
                            Bỏ ghép
                          </SubLedgerRowAction>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detail && detail.summary.missedInBank > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">
            CK trên hệ thống chưa có trên sao kê ({detail.summary.missedInBank})
          </h4>
          <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
            {detail.ledgerEntries
              .filter((row) => !row.matchedStatementLineId && (row.bankIncome > 0 || row.bankExpense > 0))
              .slice(0, 30)
              .map((row) => (
                <li key={row.id} className="flex justify-between gap-2 border-b border-gray-100 py-1">
                  <span className="min-w-0">
                    {formatDateTime(row.actualDate)} ·{' '}
                    {row.viewType ? (
                      <button
                        type="button"
                        className="text-cyan-700 hover:underline text-left"
                        onClick={() =>
                          setVoucherPreview({
                            entryId: row.id,
                            title: resolveLedgerVoucherTitle(row),
                          })
                        }
                      >
                        {row.voucherName}
                        {row.voucherReceiptRef ? ` (${row.voucherReceiptRef})` : ''}
                      </button>
                    ) : (
                      <>
                        {row.voucherName}
                        {row.voucherReceiptRef ? ` (${row.voucherReceiptRef})` : ''}
                      </>
                    )}
                  </span>
                  <span className="whitespace-nowrap">
                    {row.bankIncome > 0 ? `+${formatCurrency(row.bankIncome)}` : `-${formatCurrency(row.bankExpense)}`}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {manualMatchLineId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Ghép dòng sao kê với sổ thu chi</h3>
            <select
              value={manualLedgerId}
              onChange={(e) => setManualLedgerId(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            >
              <option value="">Chọn dòng sổ thu chi</option>
              {unmatchedLedgerOptions.map((row) => (
                <option key={row.id} value={row.id}>
                  {formatDateTime(row.actualDate)} · {row.voucherName} ·{' '}
                  {row.bankIncome > 0 ? formatCurrency(row.bankIncome) : formatCurrency(row.bankExpense)}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 border rounded-lg text-sm"
                onClick={() => setManualMatchLineId(null)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm"
                disabled={!manualLedgerId}
                onClick={async () => {
                  try {
                    const data = await incomeExpenseApi.matchProjectBankStatementLine(
                      manualMatchLineId,
                      manualLedgerId,
                    );
                    setDetail(data);
                    setManualMatchLineId(null);
                    toast.success('Đã ghép');
                  } catch (error: unknown) {
                    toast.error(getApiErrorMessage(error, 'Ghép thất bại'));
                  }
                }}
              >
                Ghép
              </button>
            </div>
          </div>
        </div>
      )}

      {createVoucher && (
        <CreateActivityVoucherModal
          kind={createVoucher.kind}
          buildingId={buildingId}
          prefill={buildPrefill(createVoucher.line)}
          onClose={() => setCreateVoucher(null)}
          onCreated={(result) => handleVoucherCreated(createVoucher.line.id, result)}
        />
      )}

      {importModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Tải sao kê {statementBankLabel}</h3>
            <p className="text-sm text-gray-600">
              Chọn kỳ sao kê (tháng/năm) tương ứng file Excel. Nhập lại cùng kỳ sẽ thay thế dữ liệu cũ.
            </p>
            <div className="flex gap-2">
              <select
                value={importMonth}
                onChange={(e) => setImportMonth(Number(e.target.value))}
                className="flex-1 rounded-lg border px-3 py-2 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    Tháng {m}
                  </option>
                ))}
              </select>
              <select
                value={importYear}
                onChange={(e) => setImportYear(Number(e.target.value))}
                className="flex-1 rounded-lg border px-3 py-2 text-sm"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center justify-center w-full px-4 py-3 rounded-lg border-2 border-dashed border-orange-300 text-orange-700 text-sm font-medium cursor-pointer hover:bg-orange-50">
              {uploading ? 'Đang tải lên...' : 'Chọn file .xls / .xlsx'}
              <input
                type="file"
                accept=".xls,.xlsx"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                  e.target.value = '';
                }}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 border rounded-lg text-sm"
                disabled={uploading}
                onClick={() => setImportModalOpen(false)}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {surplusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Thu dư (đóng dư CK)</h3>
            <p className="text-sm text-gray-600 line-clamp-3">{surplusModal.line.content}</p>
            <p className="text-sm">
              CK sao kê: <strong>{formatCurrency(surplusModal.line.amount)}</strong>
            </p>
            <label className="block text-sm">
              <span className="text-gray-600">Phiếu thu / căn hộ</span>
              <select
                value={surplusModal.billId}
                onChange={(e) =>
                  setSurplusModal((prev) => (prev ? { ...prev, billId: e.target.value } : prev))
                }
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              >
                <option value="">— Chọn phiếu —</option>
                {surplusModal.options.map((opt) => (
                  <option key={opt.billId} value={opt.billId}>
                    {opt.receiptNumber || opt.billId}
                    {opt.apartmentCode ? ` · ${opt.apartmentCode}` : ''} — net CK{' '}
                    {formatCurrency(opt.netTransfer)} → thu dư {formatCurrency(opt.gap)}
                  </option>
                ))}
              </select>
            </label>
            {surplusModal.billId && (
              <p className="text-xs text-violet-800 bg-violet-50 rounded px-3 py-2">
                Hệ thống ghi <strong>đóng dư phiếu thu</strong> cùng ngày thu phí gốc, cộng vào tài
                khoản căn hộ, rồi ghép lại sao kê.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 border rounded-lg text-sm"
                disabled={surplusSaving}
                onClick={() => setSurplusModal(null)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm disabled:opacity-50"
                disabled={surplusSaving || !surplusModal.billId}
                onClick={async () => {
                  if (!surplusModal.billId) return;
                  setSurplusSaving(true);
                  try {
                    const data = await incomeExpenseApi.createProjectBankStatementSurplusCredit(
                      surplusModal.line.id,
                      surplusModal.billId,
                    );
                    setDetail(data);
                    setSurplusModal(null);
                    toast.success('Đã ghi thu dư và cập nhật ghép');
                  } catch (error: unknown) {
                    toast.error(getApiErrorMessage(error, 'Không thể ghi thu dư'));
                  } finally {
                    setSurplusSaving(false);
                  }
                }}
              >
                {surplusSaving ? 'Đang ghi...' : 'Ghi thu dư & ghép'}
              </button>
            </div>
          </div>
        </div>
      )}

      {voucherPreview && (
        <LedgerVoucherModal
          entryId={voucherPreview.entryId}
          title={voucherPreview.title}
          mode={voucherPreview.mode}
          onClose={() => setVoucherPreview(null)}
        />
      )}

      {feePaymentModal && (
        <ApartmentServiceFeePaymentModal
          isOpen
          apartmentId={feePaymentModal.apartmentId}
          apartmentCode={feePaymentModal.apartmentCode}
          initialAmountReceived={feePaymentModal.line.amount}
          initialPaymentMethod="BANK_TRANSFER"
          initialPaymentDate={toDateInputValue(feePaymentModal.line.transactionAt)}
          initialProjectBankAccountId={account.id}
          onClose={() => setFeePaymentModal(null)}
          onSuccess={async () => {
            setFeePaymentModal(null);
            toast.success('Đã thanh toán phí — bấm Ghép tự động lại nếu dòng chưa khớp');
            if (selectedPeriodId) {
              setDetail(await incomeExpenseApi.getProjectBankStatementPeriod(selectedPeriodId));
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectBankSubLedgerPanel;
